import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { reportsApi } from '../services/reportsApi'

const REPORTS_KEY = ['reports']

const errMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message || error?.message || fallback

/**
 * Whether to show the Reports navigation item.
 *
 * Asked once and cached for the session: group membership does not change
 * mid-visit, and this runs on every page because the navigation is global.
 * Failure resolves to hidden rather than throwing — a navigation item is not
 * worth an error toast, and showing a link to a section the user cannot use is
 * worse than omitting it.
 */
export const useReportsAccess = () =>
  useQuery({
    queryKey: [...REPORTS_KEY, 'access'],
    queryFn: () => reportsApi.access().catch(() => false),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })

/**
 * The report's settings, read-only.
 *
 * Not retried and not noisy: a user with no reported group gets a 404 here,
 * which is an answer rather than an error, and the page simply omits the
 * section.
 */
export const useReportSettings = () =>
  useQuery({
    queryKey: [...REPORTS_KEY, 'settings'],
    queryFn: () => reportsApi.settings(),
    staleTime: Infinity,
    retry: false,
  })

export const useReports = (params: { page?: number; limit?: number; year?: number } = {}) =>
  useQuery({
    queryKey: [...REPORTS_KEY, params],
    queryFn: () => reportsApi.list(params),
  })

export const useReport = (id: number | undefined) =>
  useQuery({
    queryKey: [...REPORTS_KEY, 'detail', id],
    queryFn: () => reportsApi.get(id as number),
    enabled: typeof id === 'number' && !Number.isNaN(id),
    // A report is a frozen record; there is nothing to poll for.
    staleTime: Infinity,
  })

/** Versions of the period a report covers. Only asked for when one is open. */
export const useReportVersions = (id: number | undefined) =>
  useQuery({
    queryKey: [...REPORTS_KEY, 'versions', id],
    queryFn: () => reportsApi.versions(id as number),
    enabled: Number.isFinite(id),
    staleTime: Infinity,
  })

export const useDownloadReport = () =>
  useMutation({
    mutationFn: ({ id, format, fallback }: { id: number; format: 'csv' | 'pdf'; fallback?: string }) =>
      format === 'csv'
        ? reportsApi.downloadCsv(id, fallback)
        : reportsApi.downloadPdf(id, fallback),
    onError: (error: any) => toast.error(errMessage(error, 'Download failed')),
  })
